import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompactionEfficiencyChart } from "./charts/CompactionEfficiencyChart";

// A chart only starts telling a story once there are enough points to show a
// trend; below that, a lone bar (or two) reads as noise. KPI tiles scale
// down gracefully to a single compaction, so they are always shown first.
const MIN_PAIRS_FOR_CHART = 4;

const KpiTile = ({ label, value, accent = false }) => (
  <div className="flex flex-col items-center justify-center gap-1 py-3 flex-1 min-w-0">
    <span className="text-xs text-gray-400 whitespace-nowrap">{label}</span>
    <span
      className={`text-2xl font-bold whitespace-nowrap ${accent ? "text-[#00A7A6]" : "text-[#222222]"}`}
    >
      {value}
    </span>
  </div>
);

export const CompactionEfficiencySummary = ({ compactionPairs }) => {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    if (!compactionPairs?.length) return null;
    const gains = compactionPairs.map((pair) => pair.gain ?? 0);
    const lastGain = gains[0];
    const bestGain = Math.max(...gains);
    const averageGain = Math.round(
      gains.reduce((sum, value) => sum + value, 0) / gains.length,
    );
    return { lastGain, bestGain, averageGain };
  }, [compactionPairs]);

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        {t("No compaction recorded yet")}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-stretch divide-x divide-gray-200">
        <KpiTile label={t("Last gain")} value={`-${stats.lastGain}%`} accent />
        <KpiTile label={t("Average gain")} value={`-${stats.averageGain}%`} />
        <KpiTile label={t("Best gain")} value={`-${stats.bestGain}%`} />
      </div>
      {compactionPairs.length >= MIN_PAIRS_FOR_CHART && (
        <div className="border-t border-gray-200 pt-1">
          <CompactionEfficiencyChart compactionPairs={compactionPairs} />
        </div>
      )}
    </div>
  );
};
